import type { Nullable } from '~/types/generics';

export class ShortPolling<T = void> {
  private asyncFunction: (signal: AbortSignal) => Promise<T>;
  private interval: number;
  private skipFirst: boolean;
  private pollId: number;
  private timeoutId: Nullable<NodeJS.Timeout>;
  private isPolling: boolean;
  private isPaused: boolean;
  private abortController: Nullable<AbortController>;
  private timestampLastPoll: Nullable<number>;
  private timestampStarted: Nullable<number>;
  private timestampPaused: Nullable<number>;

  constructor(asyncFunction: (signal: AbortSignal) => Promise<T>, interval: number, skipFirst = false) {
    this.asyncFunction = asyncFunction;
    this.interval = interval;
    this.skipFirst = skipFirst;
    this.pollId = 0;
    this.timeoutId = null;
    this.isPolling = false;
    this.isPaused = false;
    this.abortController = null;
    this.timestampLastPoll = null;
    this.timestampStarted = null;
    this.timestampPaused = null;
  }

  public async start(): Promise<void> {
    if (this.isPolling) {
      return;
    }
    this.isPolling = true;
    this.isPaused = false;
    this.timestampStarted = Date.now();

    if (!this.skipFirst) {
      await this.executePoll();
    } else {
      this.scheduleNextPoll();
    }
  }

  public stop(): void {
    this.clearTimeout();
    this.abortOngoingRequest();
    this.isPolling = false;
    this.isPaused = false;
  }

  public pause(): void {
    if (!this.isPolling || this.isPaused) {
      return;
    }
    this.isPaused = true;
    this.timestampPaused = Date.now();

    this.clearTimeout();
    this.abortOngoingRequest();
  }

  public async resume(pollImmediate: boolean = false): Promise<void> {
    if (!this.isPolling || !this.isPaused) {
      return;
    }
    this.isPaused = false;

    if (pollImmediate) {
      await this.executePoll();
    } else {
      const timeAfterPoll = (this.timestampPaused || 0) - (this.timestampLastPoll || this.timestampStarted || 0);
      const timeRemains = this.interval - Math.max(timeAfterPoll, 0);

      this.setInterval(timeRemains, true);
    }
  }

  public async forcePoll(): Promise<void> {
    if (!this.isPolling) {
      this.isPolling = true;
      this.isPaused = false;
    }

    this.clearTimeout();
    this.abortOngoingRequest();

    await this.executePoll();
  }

  public setInterval(interval: number, once: boolean = false): void {
    const _interval = this.interval;

    this.interval = interval;
    this.clearTimeout();

    if (this.isPolling && !this.isPaused) {
      this.scheduleNextPoll();
    }

    if (once) {
      this.interval = _interval;
    }
  }

  private async executePoll(): Promise<void> {
    if (!this.isPolling || this.isPaused) {
      return;
    }

    const currentPollId = ++this.pollId;
    this.timestampLastPoll = Date.now();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    try {
      await this.asyncFunction(signal);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.log('Polling request was aborted.');
        } else {
          console.error('Polling error:', error.message);
        }
      } else {
        console.error('Unexpected error:', error);
      }
    } finally {
      this.abortController = null;
      if (this.isPolling && !this.isPaused && currentPollId === this.pollId) {
        this.scheduleNextPoll();
      }
    }
  }

  private scheduleNextPoll(): void {
    if (!this.isPolling || this.isPaused) {
      return;
    }
    this.timeoutId = setTimeout(() => void this.executePoll(), this.interval);
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private abortOngoingRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}
